import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';

const CartContext = createContext(null);

const STORAGE_KEY = 'smart-order-cart';

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return null;
}

const defaultState = {
  items: [],
  seniorMode: false,
  sessionId: null,
  tableId: null,
  storeId: null,
  paymentMode: 'post',
};

function initState() {
  const saved = loadFromStorage();
  if (saved) {
    return {
      ...defaultState,
      items: saved.items || [],
      sessionId: saved.sessionId || null,
      tableId: saved.tableId || null,
      storeId: saved.storeId || null,
      seniorMode: saved.seniorMode || false,
    };
  }
  return defaultState;
}

function reducer(state, action) {
  switch (action.type) {
    case 'ADD_ITEM': {
      const existing = state.items.findIndex(
        (it) =>
          it.menuId === action.payload.menuId &&
          JSON.stringify(it.options) === JSON.stringify(action.payload.options)
      );
      if (existing >= 0) {
        const updated = state.items.map((it, idx) => {
          if (idx === existing) {
            const qty = Math.min(99, it.quantity + action.payload.quantity);
            return { ...it, quantity: qty, subtotal: it.price * qty };
          }
          return it;
        });
        return { ...state, items: updated };
      }
      return {
        ...state,
        items: [
          ...state.items,
          {
            ...action.payload,
            subtotal: action.payload.price * action.payload.quantity,
          },
        ],
      };
    }
    case 'REMOVE_ITEM':
      return { ...state, items: state.items.filter((_, idx) => idx !== action.payload) };
    case 'UPDATE_QUANTITY': {
      const qty = Math.min(99, Math.max(1, action.payload.quantity));
      return {
        ...state,
        items: state.items.map((it, idx) =>
          idx === action.payload.index
            ? { ...it, quantity: qty, subtotal: it.price * qty }
            : it
        ),
      };
    }
    case 'CLEAR_CART':
      return { ...state, items: [] };
    case 'SET_SENIOR_MODE':
      return { ...state, seniorMode: action.payload };
    case 'SET_SESSION':
      return { ...state, sessionId: action.payload };
    case 'SET_TABLE_ID':
      return { ...state, tableId: action.payload };
    case 'SET_STORE_ID':
      return { ...state, storeId: action.payload };
    case 'SET_SESSION_INFO':
      return {
        ...state,
        sessionId: action.payload.sessionId,
        tableId: action.payload.tableId,
        storeId: action.payload.storeId,
      };
    case 'SET_PAYMENT_MODE':
      return { ...state, paymentMode: action.payload };
    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, undefined, initState);

  // Persist to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          items: state.items,
          sessionId: state.sessionId,
          tableId: state.tableId,
          storeId: state.storeId,
          seniorMode: state.seniorMode,
        })
      );
    } catch (e) {}
  }, [state.items, state.sessionId, state.tableId, state.storeId, state.seniorMode]);

  // Sync senior mode class on body
  useEffect(() => {
    if (state.seniorMode) {
      document.body.classList.add('senior-mode');
    } else {
      document.body.classList.remove('senior-mode');
    }
  }, [state.seniorMode]);

  const addItem = useCallback((item) => {
    dispatch({ type: 'ADD_ITEM', payload: item });
  }, []);

  const removeItem = useCallback((index) => {
    dispatch({ type: 'REMOVE_ITEM', payload: index });
  }, []);

  const updateQuantity = useCallback((index, quantity) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { index, quantity } });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' });
  }, []);

  const setSeniorMode = useCallback((val) => {
    dispatch({ type: 'SET_SENIOR_MODE', payload: val });
  }, []);

  const setSession = useCallback((sessionId) => {
    dispatch({ type: 'SET_SESSION', payload: sessionId });
  }, []);

  const setTableId = useCallback((tableId) => {
    dispatch({ type: 'SET_TABLE_ID', payload: tableId });
  }, []);

  const setStoreId = useCallback((storeId) => {
    dispatch({ type: 'SET_STORE_ID', payload: storeId });
  }, []);

  const setSessionInfo = useCallback((info) => {
    dispatch({ type: 'SET_SESSION_INFO', payload: info });
  }, []);

  const setPaymentMode = useCallback((mode) => {
    dispatch({ type: 'SET_PAYMENT_MODE', payload: mode });
  }, []);

  const getTotal = useCallback(() => {
    return state.items.reduce((sum, it) => sum + (it.subtotal || 0), 0);
  }, [state.items]);

  const getItemCount = useCallback(() => {
    return state.items.reduce((sum, it) => sum + (it.quantity || 0), 0);
  }, [state.items]);

  const value = {
    ...state,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    setSeniorMode,
    setSession,
    setTableId,
    setStoreId,
    setSessionInfo,
    setPaymentMode,
    getTotal,
    getItemCount,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}

export default CartContext;
